import { useCallback, useEffect, useState } from 'react';
import printingService, { type PrintDefaults } from '../services/printing.service';

/**
 * The signed-in user's default laser / label printer, fetched once per page
 * load and shared by every PrintButton on the page. `refresh()` refetches
 * (the settings drawer calls it after a save so open dialogs pick it up).
 */
const EMPTY: PrintDefaults = { laser: null, label: null };

let cached: PrintDefaults | null = null;
let inflight: Promise<PrintDefaults> | null = null;
const listeners = new Set<(d: PrintDefaults) => void>();

function load(force = false): Promise<PrintDefaults> {
  if (!force && cached) return Promise.resolve(cached);
  if (!force && inflight) return inflight;
  inflight = printingService.getDefaults()
    .then((d) => { cached = d; listeners.forEach((fn) => fn(d)); return d; })
    .catch(() => cached ?? EMPTY)
    .finally(() => { inflight = null; });
  return inflight;
}

/** Test seam: forget the cached defaults. */
export function resetPrintDefaultsCache(): void {
  cached = null;
  inflight = null;
}

export function usePrintDefaults(): { defaults: PrintDefaults; loading: boolean; refresh: () => Promise<PrintDefaults> } {
  const [defaults, setDefaults] = useState<PrintDefaults>(cached ?? EMPTY);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let alive = true;
    listeners.add(setDefaults);
    load().then((d) => { if (alive) { setDefaults(d); setLoading(false); } });
    return () => { alive = false; listeners.delete(setDefaults); };
  }, []);

  const refresh = useCallback(() => load(true), []);
  return { defaults, loading, refresh };
}
